import React, { createContext, useContext, useEffect, useState, useRef } from 'react';
import { useAuth } from './AuthContext';
import toast from 'react-hot-toast';
import { io } from 'socket.io-client';
import notificationService from '../services/notificationService';

const WebSocketContext = createContext();

export const useWebSocket = () => {
  const context = useContext(WebSocketContext);
  if (!context) {
    throw new Error('useWebSocket must be used within a WebSocketProvider');
  }
  return context;
};

export const WebSocketProvider = ({ children }) => {
  const { user, isAuthenticated } = useAuth();
  const [socket, setSocket] = useState(null);
  const [isConnected, setIsConnected] = useState(false);
  const [notifications, setNotifications] = useState([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [hasError, setHasError] = useState(false);
  const reconnectTimeoutRef = useRef(null);
  const reconnectAttempts = useRef(0);
  const maxReconnectAttempts = 5;

  // Reset error state when user changes
  useEffect(() => {
    setHasError(false);
  }, [user]);

  // If there's an error, provide a minimal context
  if (hasError) {
    console.warn('WebSocket context error detected, providing fallback');
    return (
      <WebSocketContext.Provider value={{
        socket: null,
        isConnected: false,
        notifications: [],
        unreadCount: 0,
        sendMessage: () => console.warn('WebSocket not available'),
        markNotificationAsRead: () => {},
        markAllNotificationsAsRead: () => {},
        subscribeToUpdates: () => {},
        unsubscribeFromUpdates: () => {}
      }}>
        {children}
      </WebSocketContext.Provider>
    );
  }

  // WebSocket connection function
  const connectWebSocket = () => {
    if (!isAuthenticated || !user) {
      console.log('WebSocket: User not authenticated, skipping connection');
      return;
    }

    try {
      // Fix: Remove /api from the URL since Socket.IO doesn't use it
      const serverUrl = (import.meta.env.VITE_API_URL || 'http://localhost:5000').replace('/api', '');
      console.log('🔌 Attempting Socket.IO connection to:', serverUrl);
      
      const newSocket = io(serverUrl, {
        transports: ['websocket', 'polling'],
        autoConnect: true,
        reconnection: true,
        reconnectionAttempts: maxReconnectAttempts,
        reconnectionDelay: 1000,
        reconnectionDelayMax: 5000,
        timeout: 20000,
        forceNew: true
      });

      // Add connection timeout
      const connectionTimeout = setTimeout(() => {
        console.warn('🔌 Socket.IO connection timeout, continuing without real-time features');
        setIsConnected(false);
      }, 10000);

      newSocket.on('connect', () => {
        console.log('🔌 Socket.IO connected successfully');
        clearTimeout(connectionTimeout);
        setIsConnected(true);
        reconnectAttempts.current = 0;
        
        // Join user-specific room
        if (user.id) {
          try {
            newSocket.emit('join', { userId: user.id });
            console.log('🔌 Joined user room:', user.id);
          } catch (error) {
            console.error('Error joining user room:', error);
          }
        }
        
        // Load initial notifications
        loadInitialNotifications();
      });

      newSocket.on('disconnect', (reason) => {
        console.log('🔌 Socket.IO disconnected:', reason);
        setIsConnected(false);
      });

      newSocket.on('connect_error', (error) => {
        console.error('🔌 Socket.IO connection error:', error);
        clearTimeout(connectionTimeout);
        setIsConnected(false);
        // Don't crash the app, just log the error
      });

      newSocket.on('notification', (data) => {
        try {
          console.log('🔌 Socket.IO notification received:', data);
          handleNewNotification(data);
        } catch (error) {
          console.error('Error handling notification:', error);
        }
      });

      newSocket.on('leave_update', (data) => {
        try {
          console.log('🔌 Socket.IO leave update received:', data);
          handleWebSocketMessage({ type: 'leave_update', ...data });
        } catch (error) {
          console.error('Error handling leave update:', error);
        }
      });

      newSocket.on('leave_management_update', (data) => {
        try {
          console.log('🔌 Socket.IO leave management update received:', data);
          handleWebSocketMessage({ type: 'leave_management_update', ...data });
        } catch (error) {
          console.error('Error handling leave management update:', error);
        }
      });

      setSocket(newSocket);
    } catch (error) {
      console.error('Error creating Socket.IO connection:', error);
      setIsConnected(false);
      // Don't crash the app, just log the error
    }
  };

  // Load initial notifications from API
  const loadInitialNotifications = async () => {
    try {
      console.log('🔌 Loading initial notifications...');
      const response = await notificationService.getNotifications();
      console.log('🔌 API Response:', response);
      
      if (response.success && response.data.notifications) {
        setNotifications(response.data.notifications);
        
        // Calculate unread count
        const unreadCount = response.data.notifications.filter(n => !n.isRead).length;
        setUnreadCount(unreadCount);
        
        console.log('🔌 Loaded', response.data.notifications.length, 'notifications,', unreadCount, 'unread');
      } else {
        console.log('🔌 No notifications found or API error:', response);
      }
    } catch (error) {
      console.error('Error loading initial notifications:', error);
    }
  };

  // Refresh notifications (can be called from other components)
  const refreshNotifications = async () => {
    await loadInitialNotifications();
  };

  // Handle incoming WebSocket messages
  const handleWebSocketMessage = (data) => {
    try {
      if (!data || typeof data !== 'object') {
        console.warn('Invalid WebSocket message format:', data);
        return;
      }

      switch (data.type) {
        case 'notification':
          if (data.notification) {
            handleNewNotification(data.notification);
          }
          break;
        case 'leave_request_update':
          if (data.leaveRequest) {
            handleLeaveRequestUpdate(data.leaveRequest);
          }
          break;
        case 'payroll_update':
          if (data.payroll) {
            handlePayrollUpdate(data.payroll);
          }
          break;
        case 'performance_review_update':
          if (data.review) {
            handlePerformanceReviewUpdate(data.review);
          }
          break;
        case 'document_upload':
          if (data.document) {
            handleDocumentUpload(data.document);
          }
          break;
        case 'request_update':
          if (data.request) {
            handleRequestUpdate(data.request);
          }
          break;
        case 'user_status_change':
          if (data.user) {
            handleUserStatusChange(data.user);
          }
          break;
        case 'system_alert':
          if (data.alert) {
            handleSystemAlert(data.alert);
          }
          break;
        case 'leave_update':
        case 'leave_management_update':
          // Handle leave updates from LeaveManagement component
          console.log('Leave update received:', data);
          break;
        default:
          console.log('Unknown WebSocket message type:', data.type);
      }
    } catch (error) {
      console.error('Error handling WebSocket message:', error);
      // Don't let WebSocket errors crash the app
    }
  };

  // Handle new notification
  const handleNewNotification = (notification) => {
    try {
      if (!notification || !notification.title) {
        console.warn('Invalid notification data:', notification);
        return;
      }

      setNotifications(prev => [notification, ...prev]);
      setUnreadCount(prev => prev + 1);
      
      // Show toast notification
      toast.custom((t) => (
        <div className={`${t.visible ? 'animate-enter' : 'animate-leave'} max-w-md w-full bg-white shadow-lg rounded-lg pointer-events-auto flex ring-1 ring-black ring-opacity-5`}>
          <div className="flex-1 w-0 p-4">
            <div className="flex items-start">
              <div className="flex-shrink-0">
                <div className={`w-8 h-8 rounded-full flex items-center justify-center ${
                  notification.type === 'success' ? 'bg-green-100' :
                  notification.type === 'warning' ? 'bg-yellow-100' :
                  notification.type === 'error' ? 'bg-red-100' :
                  'bg-blue-100'
                }`}>
                  {notification.type === 'success' ? '✅' :
                   notification.type === 'warning' ? '⚠️' :
                   notification.type === 'error' ? '❌' : 'ℹ️'}
                </div>
              </div>
              <div className="ml-3 flex-1">
                <p className="text-sm font-medium text-gray-900">
                  {notification.title}
                </p>
                <p className="mt-1 text-sm text-gray-500">
                  {notification.message}
                </p>
              </div>
            </div>
          </div>
          <div className="flex border-l border-gray-200">
            <button
              onClick={() => toast.dismiss(t.id)}
              className="w-full border border-transparent rounded-none rounded-r-lg p-4 flex items-center justify-center text-sm font-medium text-indigo-600 hover:text-indigo-500 focus:outline-none focus:ring-2 focus:ring-indigo-500"
            >
              Close
            </button>
          </div>
        </div>
      ), {
        duration: 5000,
        position: 'top-right',
      });
    } catch (error) {
      console.error('Error handling new notification:', error);
    }
  };

  // Handle leave request updates
  const handleLeaveRequestUpdate = (leaveRequest) => {
    try {
      if (!leaveRequest) return;
      toast.success(`Leave request ${leaveRequest.status || 'updated'} by ${leaveRequest.employeeName || 'employee'}`);
    } catch (error) {
      console.error('Error handling leave request update:', error);
    }
  };

  // Handle payroll updates
  const handlePayrollUpdate = (payroll) => {
    try {
      if (!payroll) return;
      toast.success(`Payroll ${payroll.status || 'updated'} for ${payroll.employeeName || 'employee'}`);
    } catch (error) {
      console.error('Error handling payroll update:', error);
    }
  };

  // Handle performance review updates
  const handlePerformanceReviewUpdate = (review) => {
    try {
      if (!review) return;
      toast.success(`Performance review ${review.status || 'updated'} for ${review.employeeName || 'employee'}`);
    } catch (error) {
      console.error('Error handling performance review update:', error);
    }
  };

  // Handle document uploads
  const handleDocumentUpload = (document) => {
    try {
      if (!document) return;
      toast.success(`New document uploaded: ${document.title || 'Document'}`);
    } catch (error) {
      console.error('Error handling document upload:', error);
    }
  };

  // Handle request updates
  const handleRequestUpdate = (request) => {
    try {
      if (!request) return;
      toast.success(`Request ${request.status || 'updated'}: ${request.title || 'Request'}`);
    } catch (error) {
      console.error('Error handling request update:', error);
    }
  };

  // Handle user status changes
  const handleUserStatusChange = (user) => {
    try {
      if (!user) return;
      toast.info(`${user.firstName || 'User'} ${user.lastName || ''} is now ${user.status || 'active'}`);
    } catch (error) {
      console.error('Error handling user status change:', error);
    }
  };

  // Handle system alerts
  const handleSystemAlert = (alert) => {
    try {
      if (!alert) return;
      toast.error(`System Alert: ${alert.message || 'System alert'}`, {
        duration: 8000,
      });
    } catch (error) {
      console.error('Error handling system alert:', error);
    }
  };

  // Send message through WebSocket
  const sendMessage = (message) => {
    try {
      if (socket && socket.connected) {
        socket.emit('message', message);
      } else {
        console.warn('Socket.IO is not connected, message not sent:', message);
      }
    } catch (error) {
      console.error('Error sending Socket.IO message:', error);
    }
  };

  // Mark notification as read
  const markNotificationAsRead = (notificationId) => {
    sendMessage({
      type: 'mark_notification_read',
      notificationId
    });
    
    setNotifications(prev => 
      prev.map(notif => 
        notif.id === notificationId 
          ? { ...notif, isRead: true, readAt: new Date() }
          : notif
      )
    );
    setUnreadCount(prev => Math.max(0, prev - 1));
  };

  // Mark all notifications as read
  const markAllNotificationsAsRead = () => {
    sendMessage({
      type: 'mark_all_notifications_read'
    });
    
    setNotifications(prev => 
      prev.map(notif => ({ ...notif, isRead: true, readAt: new Date() }))
    );
    setUnreadCount(0);
  };

  // Subscribe to real-time updates
  const subscribeToUpdates = (type, filters = {}) => {
    sendMessage({
      type: 'subscribe',
      subscriptionType: type,
      filters
    });
  };

  // Unsubscribe from real-time updates
  const unsubscribeFromUpdates = (type) => {
    sendMessage({
      type: 'unsubscribe',
      subscriptionType: type
    });
  };

  // Connect WebSocket when user is authenticated
  useEffect(() => {
    console.log('🔌 WebSocket useEffect triggered:', { isAuthenticated, userId: user?.id });
    
    if (isAuthenticated && user) {
      console.log('🔌 Initializing Socket.IO connection for user:', user.id);
      connectWebSocket();
      
      // Also load notifications immediately, even if WebSocket fails
      loadInitialNotifications();
    } else {
      console.log('🔌 Socket.IO: User not authenticated, skipping connection');
    }

    return () => {
      console.log('🔌 Cleaning up Socket.IO connection');
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
      if (socket) {
        socket.disconnect();
      }
    };
  }, [isAuthenticated, user]);

  // Cleanup on unmount
  useEffect(() => {
    console.log('🔌 WebSocket cleanup useEffect');
    return () => {
      if (reconnectTimeoutRef.current) {
        clearTimeout(reconnectTimeoutRef.current);
      }
    };
  }, []);

  // Provide fallback values if WebSocket fails
  const value = {
    socket: socket || null,
    isConnected: isConnected || false,
    notifications: notifications || [],
    unreadCount: unreadCount || 0,
    sendMessage: (message) => {
      try {
        if (socket && socket.readyState === WebSocket.OPEN) {
          socket.send(JSON.stringify(message));
        } else {
          console.warn('WebSocket is not connected, message not sent:', message);
        }
      } catch (error) {
        console.error('Error sending WebSocket message:', error);
      }
    },
    markNotificationAsRead: (notificationId) => {
      try {
        sendMessage({
          type: 'mark_notification_read',
          notificationId
        });
        
        setNotifications(prev => 
          prev.map(notif => 
            notif.id === notificationId 
              ? { ...notif, isRead: true, readAt: new Date() }
              : notif
          )
        );
        setUnreadCount(prev => Math.max(0, prev - 1));
      } catch (error) {
        console.error('Error marking notification as read:', error);
      }
    },
    markAllNotificationsAsRead: () => {
      try {
        sendMessage({
          type: 'mark_all_notifications_read'
        });
        
        setNotifications(prev => 
          prev.map(notif => ({ ...notif, isRead: true, readAt: new Date() }))
        );
        setUnreadCount(0);
      } catch (error) {
        console.error('Error marking all notifications as read:', error);
      }
    },
    refreshNotifications: refreshNotifications,
    subscribeToUpdates: (type, filters = {}) => {
      try {
        sendMessage({
          type: 'subscribe',
          subscriptionType: type,
          filters
        });
      } catch (error) {
        console.error('Error subscribing to updates:', error);
      }
    },
    unsubscribeFromUpdates: (type) => {
      try {
        sendMessage({
          type: 'unsubscribe',
          subscriptionType: type
        });
      } catch (error) {
        console.error('Error unsubscribing from updates:', error);
      }
    }
  };

  return (
    <WebSocketContext.Provider value={value}>
      {children}
    </WebSocketContext.Provider>
  );
}; 